import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { get } from '../../services/apiService';

/**
 * ProductionBugsByComponentChart component
 * Displays a hotspot/heatmap visualization of production defects grouped by component
 * Color intensity represents the number of defects
 * @returns {JSX.Element} ProductionBugsByComponentChart component
 */
const ProductionBugsByComponentChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await get('/api/workitems/production-bugs/public/by-component');
        
        if (response.success) {
          setData(response.data || []);
        } else {
          throw new Error(response.message || 'Failed to fetch data');
        }
      } catch (err) {
        console.error('Error fetching production bugs by component:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading chart data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="info">
        No data available for production defects by component.
      </Alert>
    );
  }

  // Prepare data and calculate color gradient
  const topData = data.slice(0, 30); // Show more items in hotspot view
  const maxCount = Math.max(...topData.map(item => item.count), 1);
  const minCount = Math.min(...topData.map(item => item.count), 0);

  // Function to calculate color intensity based on count
  const getColorIntensity = (count) => {
    if (maxCount === minCount) return 0.1; // All same value
    const normalized = (count - minCount) / (maxCount - minCount);
    return 0.1 + (normalized * 0.9); // Range from 0.1 to 1.0
  };

  // Function to get color based on intensity
  const getColor = (intensity) => {
    // Using a gradient from light blue to dark blue
    const r = Math.round(33 + (intensity - 0.1) * (13 - 33) / 0.9);
    const g = Math.round(150 + (intensity - 0.1) * (68 - 150) / 0.9);
    const b = Math.round(243 + (intensity - 0.1) * (52 - 243) / 0.9);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Production Defects by Component
      </Typography>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1,
            '& > *': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 8px)', md: '1 1 calc(20% - 8px)' },
              minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(20% - 8px)' },
              maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(20% - 8px)' }
            }
          }}
        >
          {topData.map((item, index) => {
            const originalComponent = item.component || 'Not Specified';
            // Extract only the first component if multiple are separated by semicolon
            const component = originalComponent.toString().includes(';') 
              ? originalComponent.toString().split(';')[0].trim() 
              : originalComponent;
            const count = item.count;
            const intensity = getColorIntensity(count);
            const color = getColor(intensity);
            
            return (
              <Box key={index}>
                <Tooltip 
                  title={`${originalComponent}: ${count} defect${count !== 1 ? 's' : ''}`}
                  arrow
                >
                  <Box
                    sx={{
                      backgroundColor: color,
                      color: intensity > 0.5 ? 'white' : 'rgba(0, 0, 0, 0.87)',
                      p: 1.5,
                      borderRadius: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 2
                      },
                      minHeight: 60,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {component}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </Box>
      {data.length > 30 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing top 30 components (out of {data.length} total)
        </Typography>
      )}
    </Paper>
  );
};

export default ProductionBugsByComponentChart;

